/**
 * useActivePaymentProvider — resolves the backend's configured payment
 * provider. Dashboards and billing flows read this to branch between
 * Stripe (redirect-based checkout) and Moneris (in-page tokenization).
 *
 * The value comes from the backend's PAYMENT_PROVIDER env var via the
 * activePaymentProvider GraphQL query. Callers should NOT hardcode the
 * provider client-side — runtime rollback from Stripe → Moneris must
 * flip a server env var and have the UI immediately follow.
 */

import { useQuery } from '@apollo/client';
import { GET_ACTIVE_PAYMENT_PROVIDER } from '../graphql/billingQueries';

export type PaymentProviderName = 'stripe' | 'moneris';

export interface UseActivePaymentProviderResult {
  provider: PaymentProviderName | null;
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
}

export function useActivePaymentProvider(): UseActivePaymentProviderResult {
  const { data, loading, error, refetch } = useQuery<{
    activePaymentProvider: PaymentProviderName;
  }>(GET_ACTIVE_PAYMENT_PROVIDER, {
    // network-only (not cache-first) so ops rollback of PAYMENT_PROVIDER
    // propagates to already-open browser sessions on next dashboard mount.
    // A cache-first policy would pin users to the pre-rollback gateway
    // until they hard-refresh, breaking the "single env var flip" rollback
    // guarantee the provider abstraction is supposed to provide. The
    // response is a single short string — round-trip cost is negligible.
    fetchPolicy: 'network-only',
    errorPolicy: 'all'
  });

  return {
    provider: data?.activePaymentProvider ?? null,
    loading,
    error: error as Error | undefined,
    refetch: () => {
      refetch();
    }
  };
}
